import type { NavigationGuard } from 'vue-router'

import { useRbacStore } from '@/stores/rbac'
import { useSessionStore } from '@/stores/session'

/**
 * The real `meta.permissions` guard mechanism, per MS-1's acceptance
 * criteria: "Route guards AND per-action server checks both enforce the
 * 8-role matrix (defense in depth)". This is the client half only — UX,
 * never the security boundary (admin-portal-specification.md §16.1); the
 * server re-checks every permission independently via
 * `staffauth.RequireStaffPermission` once that module exists.
 *
 * Deny-by-default: unless a route is explicitly `meta.public`, it requires
 * an authenticated `StaffPrincipal` (`useSessionStore().isAuthenticated`)
 * AND — if `meta.permissions` is set — every listed permission
 * (`useRbacStore().hasAllPermissions`). Today `principal` is always `null`
 * (see `stores/session.ts`), so every non-public route redirects to
 * `login`, proving the mechanism works even with no real backend yet.
 */
export function createAuthGuard(): NavigationGuard {
  return (to) => {
    if (to.meta.public) {
      return true
    }

    const session = useSessionStore()

    if (!session.isAuthenticated) {
      // `to.path`, never `to.fullPath`, as the redirect target: avoids ever
      // building a `redirect` query that points back at `/login` itself.
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    const required = to.meta.permissions
    if (required && required.length > 0) {
      const rbac = useRbacStore()
      if (!rbac.hasAllPermissions(required)) {
        return { name: 'forbidden' }
      }
    }

    return true
  }
}
